import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import cn from 'classnames';

import Collapse from 'react-tiny-collapse';
import Transition from 'react-tiny-transition';

import ContentContainer from 'components/content-container';
import Icon from 'components/icon';
import Link from 'components/link';
import MouseoverLink from 'components/mouseover-link';
import Text from 'components/text';
import SubMenu from './sub-menu';
import ModalContext from '../../context/modal';

const getDesktopOrders = items => {
  const requested = items
    .map(({ desktopOrder }, index) => ({ desktopOrder, index }))
    .filter(({ desktopOrder }) => desktopOrder >= 1)
    .sort((a, b) => a.desktopOrder - b.desktopOrder || a.index - b.index);

  if (!requested.length) return null;

  const slots = new Array(items.length).fill(null);

  requested.forEach(({ desktopOrder, index }) => {
    let slot = Math.min(desktopOrder, items.length) - 1;
    while (slot < items.length && slots[slot] !== null) slot++;
    slots[slot === items.length ? slots.indexOf(null) : slot] = index;
  });

  const taken = new Set(slots);
  const remaining = items
    .map((item, index) => index)
    .filter(index => !taken.has(index));

  return slots
    .map(index => (index === null ? remaining.shift() : index))
    .reduce((orders, itemIndex, slot) => {
      orders[itemIndex] = slot;
      return orders;
    }, []);
};

const MainNav = ({
  bookAppointmentLink,
  findStoreLink,
  hideMobileMenu,
  isDesktop,
  isExpanded = false,
  items
}) => {
  const raf = React.useRef();
  const [activeSubMenu, setActiveSubMenu] = React.useState('');
  const [shouldAnimateSubMenu, setShouldAnimateSubMenu] = React.useState(false);

  const hideSubMenus = () => {
    setShouldAnimateSubMenu(true);
    raf.current = requestAnimationFrame(() => setActiveSubMenu(''));
  };

  const openSubMenu = menuName => {
    const hasActiveSubMenu = !!activeSubMenu;

    setShouldAnimateSubMenu(!hasActiveSubMenu);

    if (menuName === activeSubMenu) {
      hideSubMenus();
    } else {
      raf.current = requestAnimationFrame(() => setActiveSubMenu(menuName));
    }
  };

  // Cleanup on unmount
  React.useEffect(() => () => cancelAnimationFrame(raf.current), []);

  const shouldAnimate = shouldAnimateSubMenu || !isDesktop;
  const WrapperElement = isDesktop ? 'div' : Transition;
  const hasActiveSubMenu = !!activeSubMenu;
  const desktopOrders = React.useMemo(() => getDesktopOrders(items), [items]);
  const modalContext = useContext(ModalContext);
  const toggleAppointmentBookingModal = e => {
    e.preventDefault();
    modalContext.toggleAppointmentBookingModal();
  };
  const openFindStorePageAndTrack = e => {
    e.preventDefault();
    window.__rpta = window.__rpta || [];
    window.__rpta.push('event', 'Finn butikk');

    // Navigate after the event has been pushed to ensure it gets recorded properly.
    window.location.href = findStoreLink.url;
  };

  return (
    <React.Fragment>
      <WrapperElement>
        <div
          className={cn('main-nav', {
            'is-expanded': !isDesktop && isExpanded
          })}
        >
          <ContentContainer className="main-nav-content">
            <ul className="main-nav-items">
              {items.map(({ link, content, isHighlighted }, index) => {
                const isActive = activeSubMenu === link.url;

                return (
                  <li
                    key={link.url}
                    className={cn({ 'is-highlighted': isHighlighted })}
                    style={
                      desktopOrders
                        ? { '--main-nav-item-order': desktopOrders[index] }
                        : undefined
                    }
                  >
                    <MouseoverLink
                      data-test-main-nav-link={link.text}
                      className={cn('main-nav-link', {
                        'is-active': isActive
                      })}
                      onMousehold={() => openSubMenu(link.url)}
                      {...link}
                    />

                    <div
                      className={cn('main-nav-button', {
                        'is-active': isActive
                      })}
                    >
                      <span className="main-nav-button-inner">
                        <a
                          href={link.url}
                          className="main-nav-button-inner-link"
                        >
                          <Text.span
                            theme={
                              isHighlighted
                                ? Text.themes.boldUppercaseSmall
                                : Text.themes.uppercaseSmall
                            }
                          >
                            {link.text}
                          </Text.span>
                        </a>
                        <button
                          onClick={() => openSubMenu(link.url)}
                          className="main-nav-button-inner-button"
                        >
                          <Icon name="chevron-down" />
                        </button>
                      </span>
                    </div>
                    {shouldAnimate ? (
                      <Collapse isOpen={isActive} className="main-nav-submenu">
                        <SubMenu
                          hide={hideSubMenus}
                          {...content}
                          isActive={isActive}
                        />
                      </Collapse>
                    ) : (
                      <div
                        className={cn('main-nav-submenu', {
                          'main-nav-submenu--active': isActive
                        })}
                      >
                        <SubMenu
                          hide={hideSubMenus}
                          isActive={isActive}
                          {...content}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="main-nav-buttons">
              {findStoreLink && (
                <Link
                  className="main-nav-find-store"
                  iconBefore="pin"
                  themes={[Link.themes.buttonWhite]}
                  onClick={openFindStorePageAndTrack}
                  {...findStoreLink}
                />
              )}

              {bookAppointmentLink && (
                <Link
                  isNoFollow={true}
                  className="main-nav-book-appointment"
                  themes={[Link.themes.buttonCyan]}
                  onClick={toggleAppointmentBookingModal}
                  {...bookAppointmentLink}
                />
              )}
            </div>
          </ContentContainer>
        </div>
      </WrapperElement>

      <Transition duration={500}>
        {isDesktop && hasActiveSubMenu && (
          <div className="main-nav-overlay" onClick={hideSubMenus} />
        )}
      </Transition>

      <Transition duration={500}>
        {!isDesktop && isExpanded && (
          <div className="main-nav-overlay" onClick={hideMobileMenu} />
        )}
      </Transition>
    </React.Fragment>
  );
};

MainNav.propTypes = {
  bookAppointmentLink: PropTypes.exact(Link.propTypes),
  findStoreLink: PropTypes.exact(Link.propTypes),
  hideMobileMenu: PropTypes.func,
  isDesktop: PropTypes.bool,
  isExpanded: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.exact({
      link: PropTypes.exact({
        url: PropTypes.string,
        text: PropTypes.string
      }),
      content: PropTypes.exact(SubMenu.propTypes),
      isHighlighted: PropTypes.bool,
      desktopOrder: PropTypes.number.isRequired
    })
  )
};

MainNav.propTypesMeta = {
  isDesktop: 'exclude',
  isExpanded: 'exclude'
};

MainNav.defaultProps = {
  items: []
};

export default MainNav;
